import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoShopifyCountryCode } from "@redotech/redo-model/fulfillments/fulfillment-country-code";
import { Step } from "@redotech/redo-model/return-flow";
import {
  AddressAutocompleteActions,
  AddressAutocompleteFields,
  AddressAutocompleteForm,
  createAddressAutocompleteForm,
} from "@redotech/redo-web/address-autocomplete-form";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useMemo } from "react";
import { getCustomAddressesQueryKey } from "../shipping/hooks/use-custom-addresses";

interface AddLocationModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onSave: (location: Step.LocationWithAddress) => void;
  initialLocation?: Step.LocationWithAddress | null;
}

const useAddressAutocompleteActions = (): AddressAutocompleteActions => {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const getAddressSuggestionsFromAddressLine = useHandler(
    async (query: string, sessionToken?: string) => {
      const { suggestions } = await rpcClient.getAutocompleteSuggestions({
        query,
        sessionToken,
      });
      return suggestions.map((suggestion) => ({
        keyToFullObject: suggestion.keyToFullObject,
        displayText: suggestion.displayText,
      }));
    },
  );

  const getAddressFromAddressLineSuggestion = useHandler(
    async (suggestionId: string) => {
      const { address } = await rpcClient.getAddressFromSuggestion({
        suggestionId,
      });
      return address as AddressAutocompleteFields | null;
    },
  );

  return {
    getAddressSuggestionsFromAddressLine,
    getAddressFromAddressLineSuggestion,
    getAddressSuggestionsFromName: async () => [],
    getAddressFromNameSuggestion: async () => ({
      name: "",
      address1: "",
      address2: "",
      city: "",
      province: "",
      zip: "",
      countryCode: RedoShopifyCountryCode.Us,
    }),
  };
};

export const AddLocationModal = memo(function AddLocationModal({
  open,
  setOpen,
  onSave,
  initialLocation,
}: AddLocationModalProps) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const queryClient = useQueryClient();

  const addressForm = useInput(
    useMemo(() => createAddressAutocompleteForm(false, true), []),
    {
      name: "",
      address1: "",
      address2: null,
      city: "",
      company: null,
      province: null,
      zip: "",
      countryCode: RedoShopifyCountryCode.Us,
      phone: null,
      email: null,
    },
  );

  const actions = useAddressAutocompleteActions();

  useEffect(() => {
    if (initialLocation) {
      addressForm.setValue({
        name: initialLocation.name,
        address1: initialLocation.address.street1,
        address2: initialLocation.address.street2 || null,
        city: initialLocation.address.city,
        company: null,
        province: initialLocation.address.state || null,
        zip: initialLocation.address.zip,
        countryCode: initialLocation.address.country as RedoShopifyCountryCode,
        phone: initialLocation.address.phone || null,
        email: null,
      });
    } else {
      addressForm.setValue({
        name: "",
        address1: "",
        address2: null,
        city: "",
        company: null,
        province: null,
        zip: "",
        countryCode: RedoShopifyCountryCode.Us,
        phone: null,
        email: null,
      });
    }
    // todo resolve issue with unable to fill out forms without needing this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation, open]);

  const handleSave = async () => {
    let locationId: string | undefined = initialLocation?.locationId;
    const externalLocationId: string | undefined =
      initialLocation?.externalLocationId;

    const formValue = addressForm.value;

    if (!initialLocation) {
      try {
        const response = await rpcClient.createAddress({
          address: {
            name: formValue.name,
            street1: formValue.address1,
            street2: formValue.address2 || undefined,
            city: formValue.city,
            state: formValue.province || undefined,
            zip: formValue.zip,
            country: formValue.countryCode,
            phone: formValue.phone || undefined,
          },
        });
        locationId = response.addressId;

        await queryClient.invalidateQueries({
          queryKey: getCustomAddressesQueryKey(),
        });
      } catch (error) {
        console.error("Failed to save custom address:", error);
      }
    }

    const location: Step.LocationWithAddress = {
      name: formValue.name,
      address: {
        street1: formValue.address1,
        street2: formValue.address2 || undefined,
        city: formValue.city,
        state: formValue.province || "",
        zip: formValue.zip,
        country: formValue.countryCode,
        phone: formValue.phone || undefined,
      },
      locationId,
      externalLocationId,
    };

    onSave(location);
    setOpen(false);
  };

  const isValid = addressForm.allErrors.length === 0;

  return (
    <RedoModal
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={() => setOpen(false)}
      primaryButton={{
        size: "sm",
        text: initialLocation ? "Update location" : "Add location",
        onClick: handleSave,
        disabled: !isValid,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: () => setOpen(false),
        size: "sm",
      }}
      title={`${initialLocation ? "Edit" : "Add"} custom location`}
    >
      <Flex dir="column" gap="lg" w="full">
        <AddressAutocompleteForm
          actions={actions}
          autoCompleteOnName={false}
          form={addressForm}
          hideCompanyField
          hideEmailField
          includeNameField
          requirePhone
          title="Location information"
        />
      </Flex>
    </RedoModal>
  );
});
